/**
 * Fixed 3D terrain COG asset IDs (tiling_service.assets).
 * Override at build time: REACT_APP_3D_DEM_ASSET_ID, REACT_APP_3D_ORTHOPHOTO_ASSET_ID
 * Defaults match current Minto DEM (16) and orthophoto (18).
 */
function parsePositiveAssetId(raw, fallback) {
  const n = parseInt(String(raw ?? '').trim(), 10);
  if (Number.isFinite(n) && n > 0) return n;
  return fallback;
}

export const CONFIGURED_DEM_ASSET_ID = parsePositiveAssetId(
  process.env.REACT_APP_3D_DEM_ASSET_ID,
  16,
);

export const CONFIGURED_ORTHO_ASSET_ID = parsePositiveAssetId(
  process.env.REACT_APP_3D_ORTHOPHOTO_ASSET_ID,
  18,
);

/** DEM / terrain native EPSG (must match ortho tiling native CRS for UV math). */
export const CONFIGURED_DEM_NATIVE_EPSG = (() => {
  const raw = String(process.env.REACT_APP_DEM_NATIVE_EPSG ?? '26908').trim();
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n <= 0 || n > 99999) return 26908;
  return n;
})();

/** When true (default), orthophoto uses wolfwalker raster_tiles_proxy XYZ; else COG via tiling_cog_proxy. */
export const ORTHO_USE_XYZ_TILES = String(process.env.REACT_APP_ORTHO_USE_XYZ_TILES || 'true').toLowerCase() !== 'false';

/**
 * When true, XYZ tile requests use TMS row index: yUrl = (2^z - 1) - ty while keeping slippy canvas layout.
 * Set if RasterTileApi serves TMS Y while we compute slippy ty.
 */
export const ORTHO_XYZ_TMS_Y = String(process.env.REACT_APP_ORTHO_XYZ_TMS_Y ?? '')
  .trim()
  .toLowerCase() === 'true';

/**
 * When true, try BFF GET /api/raster_ortho_window (GDAL warp in native SRID) before XYZ tiles / COG.
 * Function App: RASTER_ORTHO_WINDOW_UPSTREAM, RASTER_TILE_API_KEY; optional RASTER_ORTHO_WINDOW_API_KEY.
 */
export const ORTHO_USE_SERVER_WARP_WINDOW = String(process.env.REACT_APP_ORTHO_USE_SERVER_WARP_WINDOW ?? '')
  .trim()
  .toLowerCase() === 'true';

function parseHexColor(raw, fallback) {
  const s = String(raw ?? '').trim();
  if (!s) return fallback;
  const hex = s.startsWith('0x') || s.startsWith('0X') ? s.slice(2) : s.replace(/^#/, '');
  const n = parseInt(hex, 16);
  if (!Number.isFinite(n) || n < 0 || n > 0xffffff) return fallback;
  return n;
}

function parseUnitFloat(raw, fallback) {
  const n = parseFloat(String(raw ?? '').trim());
  if (!Number.isFinite(n) || n <= 0 || n > 1) return fallback;
  return n;
}

/** RGB fill for terrain back faces when orthophoto is draped (not mirrored on underside). */
export const TERRAIN_UNDERSIDE_COLOR = parseHexColor(
  process.env.REACT_APP_TERRAIN_UNDERSIDE_COLOR,
  0x3d4f3f,
);

/** Reserved; ortho underside is forced to alpha 1 in the terrain shader (fully opaque). */
export const TERRAIN_UNDERSIDE_OPACITY = parseUnitFloat(
  process.env.REACT_APP_TERRAIN_UNDERSIDE_OPACITY,
  0.9,
);

/**
 * Initial ortho terrain debug mode (optional). Prefer the 3D viewer toolbar
 * “Ortho underside” menu (no rebuild). REACT_APP_TERRAIN_DEBUG_PROBE:
 * unset / "0" — default On
 * "normals" | "1" — start in world-normals debug
 * "underside" | "2" — start in underside-mask debug
 */
export const TERRAIN_DEBUG_PROBE = (() => {
  const v = String(process.env.REACT_APP_TERRAIN_DEBUG_PROBE ?? '')
    .trim()
    .toLowerCase();
  if (v === 'normals' || v === '1') return 1;
  if (v === 'underside' || v === 'ndotv' || v === '2') return 2;
  if (v === '0' || v === '' || v === 'false') return 0;
  return 0;
})();

/** Console-only normal stats after DEM mesh build (no shader change). */
export const TERRAIN_LOG_NORMALS = String(
  process.env.REACT_APP_TERRAIN_LOG_NORMALS ?? '',
)
  .trim()
  .toLowerCase() === 'true';

/** When true, 3D viewer uses Drillz tenant_geometry BFF instead of legacy nearby_traces/downhole_data. */
export const USE_DRILLZ_GEOMETRY = String(
  process.env.REACT_APP_3D_USE_DRILLZ_GEOMETRY ?? '',
)
  .trim()
  .toLowerCase() === 'true';
